import { useState, useEffect, useCallback } from 'react';
import { 
  Users, Search, Eye, Phone, Mail, MapPin, Building2, Store,
  ShoppingBag, IndianRupee, TrendingUp, Package, ArrowUpRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { customerAPI, storesAPI } from '@/services/api';
import { formatDate, formatCurrency } from '@/utils/dateFormat';

const HQCustomersPage = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedStoreId, setSelectedStoreId] = useState('all');
  const [stores, setStores] = useState([]);
  const [total, setTotal] = useState(0);
  const [storeSummary, setStoreSummary] = useState([]);
  
  // Analytics dialog
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  
  const { toast } = useToast();

  // Fetch stores
  useEffect(() => {
    const fetchStores = async () => {
      try {
        const response = await storesAPI.list({ is_active: true });
        setStores(response.data || []);
      } catch (error) {
        console.error('Failed to fetch stores:', error);
      }
    };
    fetchStores();
  }, []);

  // Fetch customers
  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { search };
      if (selectedStoreId !== 'all') {
        params.store_id = selectedStoreId;
      }
      const response = await customerAPI.hqListAll(params);
      setCustomers(response.data.customers || []);
      setTotal(response.data.total || 0);
      setStoreSummary(response.data.store_summary || []);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch customers', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [search, selectedStoreId, toast]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchCustomers();
    }, 300);
    return () => clearTimeout(timer);
  }, [fetchCustomers]);

  // Open analytics dialog
  const handleOpenAnalytics = async (customer) => {
    setSelectedCustomer(customer);
    setAnalyticsOpen(true);
    setLoadingAnalytics(true);
    
    try {
      const response = await customerAPI.hqGetAnalytics(customer.id);
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast({ title: 'Error', description: 'Failed to fetch customer analytics', variant: 'destructive' });
    } finally {
      setLoadingAnalytics(false);
    }
  };

  // Calculate totals
  const totalCustomers = total;
  const totalRevenue = storeSummary.reduce((sum, s) => sum + (s.total_revenue || 0), 0);

  return (
    <div className="p-6 space-y-6" data-testid="hq-customers-page">
      <Toaster />
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Users className="w-7 h-7 text-orange-500" />
            Customer Analytics
          </h1>
          <p className="text-gray-500 mt-1">View all customers across all stores</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm">Total Customers</p>
                <p className="text-3xl font-bold">{totalCustomers.toLocaleString()}</p>
              </div>
              <Users className="w-10 h-10 text-blue-200" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-emerald-100 text-sm">Total Revenue</p>
                <p className="text-3xl font-bold">{formatCurrency(totalRevenue)}</p>
              </div>
              <IndianRupee className="w-10 h-10 text-emerald-200" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm">Active Stores</p>
                <p className="text-3xl font-bold">{storeSummary.length}</p>
              </div>
              <Store className="w-10 h-10 text-purple-200" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-amber-100 text-sm">Avg. per Customer</p>
                <p className="text-3xl font-bold">
                  {formatCurrency(totalCustomers > 0 ? totalRevenue / totalCustomers : 0)}
                </p>
              </div>
              <TrendingUp className="w-10 h-10 text-amber-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Store Summary */}
      {storeSummary.length > 0 && (
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardHeader className="pb-4 border-b border-gray-100">
            <CardTitle className="text-lg text-gray-800 flex items-center gap-2">
              <Store className="w-5 h-5 text-orange-500" />
              Store-wise Customer Distribution
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {storeSummary.map((store) => (
                <div key={store._id} className="p-4 bg-gray-50 rounded-lg">
                  <p className="font-medium text-gray-800">{store.store_name || 'Unknown'}</p>
                  <p className="text-xs text-gray-500">{store.store_code}</p>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-sm text-emerald-600 font-medium">{store.customer_count} customers</span>
                    <span className="text-sm text-gray-600">{formatCurrency(store.total_revenue)}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search & Filter */}
      <Card className="bg-white border-gray-200 shadow-sm">
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, mobile, or GSTIN..."
                className="pl-10 bg-gray-50 border-gray-300"
                data-testid="search-customer-input"
              />
            </div>
            <Select value={selectedStoreId} onValueChange={setSelectedStoreId}>
              <SelectTrigger className="w-64 bg-gray-50 border-gray-300">
                <SelectValue placeholder="Filter by Store" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Stores</SelectItem>
                {stores.map(store => (
                  <SelectItem key={store.id} value={store.id}>
                    {store.name} ({store.code})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Customers Table */}
      <Card className="bg-white border-gray-200 shadow-sm">
        <CardHeader className="pb-4 border-b border-gray-100">
          <CardTitle className="text-lg text-gray-800 flex items-center gap-2">
            <Users className="w-5 h-5 text-orange-500" />
            All Customers ({total})
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : customers.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-lg font-medium">No Customers Found</p>
              <p className="text-sm mt-1">Try adjusting your search or filter</p>
            </div>
          ) : (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="text-gray-600">Customer</TableHead>
                    <TableHead className="text-gray-600">Contact</TableHead>
                    <TableHead className="text-gray-600">Store</TableHead>
                    <TableHead className="text-gray-600">GST/PAN</TableHead>
                    <TableHead className="text-gray-600 text-center">Bills</TableHead>
                    <TableHead className="text-gray-600 text-right">Total Amount</TableHead>
                    <TableHead className="text-gray-600 text-center">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {customers.map((customer) => (
                    <TableRow key={customer.id} className="border-gray-200 hover:bg-gray-50">
                      <TableCell>
                        <p className="font-medium text-gray-800">{customer.name}</p>
                        <p className="text-xs text-gray-500">
                          {customer.state && `${customer.city || customer.district || ''}, ${customer.state}`}
                        </p>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-gray-600">
                          <Phone className="w-3 h-3" />
                          <span className="text-sm">{customer.mobile}</span>
                        </div>
                        {customer.email && (
                          <div className="flex items-center gap-1 text-gray-500 text-xs mt-1">
                            <Mail className="w-3 h-3" />
                            <span>{customer.email}</span>
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          <Store className="w-3 h-3 mr-1" />
                          {customer.created_at_store_code || customer.created_at_store_name || '-'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {customer.gstin ? (
                          <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                            GST
                          </Badge>
                        ) : customer.pan ? (
                          <Badge variant="outline" className="text-xs">PAN</Badge>
                        ) : (
                          <span className="text-gray-400 text-sm">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="font-medium text-emerald-600">{customer.total_purchases || 0}</span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="font-medium text-gray-800">
                          {formatCurrency(customer.total_spent || 0)}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleOpenAnalytics(customer)}
                          className="text-orange-600 border-orange-200 hover:bg-orange-50"
                          data-testid={`view-analytics-${customer.id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Analytics
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Customer Analytics Dialog */}
      <Dialog open={analyticsOpen} onOpenChange={setAnalyticsOpen}>
        <DialogContent className="bg-white border-gray-200 max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-gray-800 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-orange-500" />
              Customer Analytics
            </DialogTitle>
          </DialogHeader>
          
          {loadingAnalytics ? (
            <div className="text-center py-8 text-gray-500">Loading analytics...</div>
          ) : analytics ? (
            <div className="space-y-6">
              {/* Customer Info */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-800">{analytics.customer?.name}</h3>
                    <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                      <span className="flex items-center gap-1">
                        <Phone className="w-4 h-4" />
                        {analytics.customer?.mobile}
                      </span>
                      {analytics.customer?.email && (
                        <span className="flex items-center gap-1">
                          <Mail className="w-4 h-4" />
                          {analytics.customer?.email}
                        </span>
                      )}
                    </div>
                    {analytics.customer?.gstin && (
                      <Badge variant="outline" className="mt-2 bg-blue-50 text-blue-700 border-blue-200">
                        <Building2 className="w-3 h-3 mr-1" />
                        GSTIN: {analytics.customer.gstin}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-emerald-50 p-4 rounded-lg text-center">
                  <ShoppingBag className="w-8 h-8 mx-auto text-emerald-500 mb-2" />
                  <p className="text-3xl font-bold text-emerald-700">{analytics.stats?.total_bills || 0}</p>
                  <p className="text-sm text-emerald-600">Total Bills</p>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg text-center">
                  <IndianRupee className="w-8 h-8 mx-auto text-blue-500 mb-2" />
                  <p className="text-3xl font-bold text-blue-700">{formatCurrency(analytics.stats?.total_amount || 0)}</p>
                  <p className="text-sm text-blue-600">Total Amount</p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg text-center">
                  <TrendingUp className="w-8 h-8 mx-auto text-purple-500 mb-2" />
                  <p className="text-3xl font-bold text-purple-700">{formatCurrency(analytics.stats?.avg_bill_value || 0)}</p>
                  <p className="text-sm text-purple-600">Avg. Bill Value</p>
                </div>
              </div>

              {/* Store Breakdown */}
              {analytics.store_breakdown?.length > 0 && (
                <div className="border border-gray-200 rounded-lg">
                  <div className="p-3 bg-gray-50 border-b border-gray-200">
                    <h4 className="font-medium text-gray-800 flex items-center gap-2">
                      <Store className="w-4 h-4 text-orange-500" />
                      Store-wise Purchases
                    </h4>
                  </div>
                  <div className="p-4">
                    <div className="grid grid-cols-2 gap-3">
                      {analytics.store_breakdown.map((store, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                          <div>
                            <p className="font-medium text-gray-800">{store.store_name}</p>
                            <p className="text-xs text-gray-500">{store.store_code}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-medium text-emerald-600">{store.bill_count} bills</p>
                            <p className="text-sm text-gray-600">{formatCurrency(store.total_amount)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Top 50 Products */}
              <div className="border border-gray-200 rounded-lg">
                <div className="p-3 bg-gray-50 border-b border-gray-200">
                  <h4 className="font-medium text-gray-800 flex items-center gap-2">
                    <Package className="w-4 h-4 text-orange-500" />
                    Top 50 Products (Most Purchased)
                  </h4>
                </div>
                <div className="p-4">
                  {analytics.top_50_products?.length > 0 ? (
                    <div className="max-h-96 overflow-y-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-gray-50">
                            <TableHead className="text-gray-600">#</TableHead>
                            <TableHead className="text-gray-600">Product</TableHead>
                            <TableHead className="text-gray-600 text-center">Times Bought</TableHead>
                            <TableHead className="text-gray-600 text-center">Total Qty</TableHead>
                            <TableHead className="text-gray-600 text-right">Total Amount</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {analytics.top_50_products.map((product, idx) => (
                            <TableRow key={idx} className="border-gray-200">
                              <TableCell className="font-medium text-gray-500">{idx + 1}</TableCell>
                              <TableCell>
                                <p className="font-medium text-gray-800">{product.product_name}</p>
                                {product.sku && <p className="text-xs text-gray-500">SKU: {product.sku}</p>}
                              </TableCell>
                              <TableCell className="text-center">
                                <Badge variant="outline" className="bg-emerald-50 text-emerald-700">
                                  {product.purchase_count}x
                                </Badge>
                              </TableCell>
                              <TableCell className="text-center text-gray-600">{product.total_qty}</TableCell>
                              <TableCell className="text-right font-medium text-gray-800">
                                {formatCurrency(product.total_amount)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <p className="text-center text-gray-500 py-4">No purchase data available</p>
                  )}
                </div>
              </div>

              {/* Recent Purchases */}
              <div className="border border-gray-200 rounded-lg">
                <div className="p-3 bg-gray-50 border-b border-gray-200">
                  <h4 className="font-medium text-gray-800 flex items-center gap-2">
                    <ShoppingBag className="w-4 h-4 text-orange-500" />
                    Recent Purchases
                  </h4>
                </div>
                <div className="p-4">
                  {analytics.recent_purchases?.length > 0 ? (
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {analytics.recent_purchases.map((sale, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                          <div>
                            <p className="font-medium text-emerald-600">{sale.bill_number}</p>
                            <p className="text-xs text-gray-500">{formatDate(sale.bill_datetime)}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-medium text-gray-800">{formatCurrency(sale.grand_total)}</p>
                            <p className="text-xs text-gray-500">{sale.items?.length || 0} items</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-gray-500 py-4">No recent purchases</p>
                  )}
                </div>
              </div>
            </div>
          ) : null}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setAnalyticsOpen(false)} className="border-gray-300">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HQCustomersPage;
